import React, { useEffect, useState } from 'react';
import '../../pages/SHL-02/SHL-02_global-overlays.css';
import { api, call, type Schemas } from '../../api/client';
import { useApi } from '../../api/query';
import { t } from '../../i18n';
import { clock, day } from '../../lib/format';
import { rich } from '../../lib/rich';

type Item = Schemas['WhatsNewItem'];

const TYPE_LABEL: Record<Item['type'], () => string> = {
  webinar: () => t('app.wn.webinar', 'Webinar'),
  new_service: () => t('app.wn.new-service', 'New service'),
  announcement: () => t('app.wn.announcement', 'Announcement'),
  release_notes: () => t('app.wn.release-notes', 'Release notes'),
};
const TYPE_EB: Record<Item['type'], () => string> = {
  webinar: () => t('app.wn.eb-webinar', 'LIVE WEBINAR'),
  new_service: () => t('app.wn.eb-service', 'NEW SERVICE'),
  announcement: () => t('app.wn.eb-announcement', 'ANNOUNCEMENT'),
  release_notes: () => t('app.wn.eb-release', 'RELEASE NOTES'),
};

// What was closed in this session. It lives outside the component because a language change
// remounts the shell, and the modal would open again.
let closed: { session?: string; ids: Set<string> } = { ids: new Set() };

/**
 * What's New important modal (SHL-02): items flagged important by Dux FX are shown once per user
 * (`GET /whats-new/important`); "Remind me later" brings it back at the next sign-in, closing it
 * records the dismissal (`POST /whats-new/{id}/modal`).
 */
export function WhatsNewModal({ enabled, session }: { enabled: boolean; session?: string }) {
  const q = useApi(['whats-new', 'important'], () => call(api.GET('/whats-new/important')), { enabled, staleTime: Infinity });
  const [, setRev] = useState(0);
  if (closed.session !== session) closed = { session, ids: new Set() };
  const item = q.data?.items?.find((i) => !closed.ids.has(i.id));

  const close = (action: 'dismiss' | 'remind_later') => {
    if (!item) return;
    closed.ids.add(item.id);
    setRev((r) => r + 1);
    void call(api.POST('/whats-new/{id}/modal', { params: { path: { id: item.id } }, body: { action } })).catch(() => {});
  };
  useEffect(() => {
    if (!item) return;
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && close('remind_later');
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  });
  if (!item) return null;

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const start = item.event_start ? new Date(item.event_start) : null;
  const mins = start && item.event_end ? Math.round((Date.parse(item.event_end) - start.getTime()) / 60000) : null;
  const when = start ? day(start) : null;
  const time = start ? clock(start, undefined, false) : '';
  const cta = item.cta?.[0];
  const external = cta && /^https?:/i.test(cta.url);

  return (
    <div
      className="bx-scrim shl2-scrim"
      onClick={(e) => {
        if (e.target === e.currentTarget) close('remind_later');
      }}
    >
      <div className="bx-dialog bx-dialog--wide" role="dialog" aria-modal="true" aria-labelledby="news-t2">
        <div className="shl2-hero">
          <div className="shl2-hero-art" aria-hidden="true">
            <div className="shl2-hero-in">
              <svg width="440" height="156" viewBox="0 0 440 156">
                <defs>
                  <linearGradient id="shl2-fb" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" style={{ stopColor: 'var(--chart-1)', stopOpacity: '0.26' }} />
                    <stop offset="1" style={{ stopColor: 'var(--chart-1)', stopOpacity: '0' }} />
                  </linearGradient>
                </defs>
                <path d="M0 40.5H440M0 80.5H440M0 120.5H440" style={{ stroke: 'var(--gold-border)' }} fill="none" />
                <rect x="248" y="0" width="64" height="156" style={{ fill: 'color-mix(in srgb, var(--warn) 11%, transparent)' }} />
                <path d="M262 30V156" style={{ stroke: 'var(--ink-2)' }} strokeDasharray="3 3" fill="none" opacity="0.55" />
                <path
                  d="M0 104 L16 101 L32 105 L48 99 L64 102 L80 97 L96 101 L112 95 L128 99 L144 94 L160 98 L176 93 L192 97 L208 92 L224 96 L240 93 L254 95 L262 93 L266 58 L271 68 L277 44 L284 60 L291 48 L299 64 L308 54 L318 67 L329 58 L341 70 L354 62 L367 72 L381 66 L395 74 L409 68 L424 72 L424 156 L0 156 Z"
                  fill="url(#shl2-fb)"
                />
                <path
                  d="M0 104 L16 101 L32 105 L48 99 L64 102 L80 97 L96 101 L112 95 L128 99 L144 94 L160 98 L176 93 L192 97 L208 92 L224 96 L240 93 L254 95 L262 93 L266 58 L271 68 L277 44 L284 60 L291 48 L299 64 L308 54 L318 67 L329 58 L341 70 L354 62 L367 72 L381 66 L395 74 L409 68 L424 72"
                  style={{ stroke: 'var(--chart-1)' }}
                  strokeWidth="1.8"
                  fill="none"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                <circle cx="424" cy="72" r="9" style={{ fill: 'var(--chart-1)' }} fillOpacity="0.18" />
                <circle cx="424" cy="72" r="4" style={{ fill: 'var(--chart-1)' }} />
              </svg>
            </div>
          </div>
          <div className="shl2-hero-id">
            <span className="shl2-hero-ic">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3 4h18v12H3zM8 20h8M12 16v4" />
                <path d="M10 7.5v5l4-2.5-4-2.5Z" />
              </svg>
            </span>
            <span className="shl2-hero-eb">{TYPE_EB[item.type]()}</span>
            <span className="bx-cap">{t('app.wn.from', 'Dux FX')}</span>
          </div>
          <button type="button" className="bx-x shl2-hero-x" aria-label={t('app.wn.close', 'Close')} onClick={() => close('dismiss')}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        <div className="bx-dialog-b" style={{ padding: '18px 24px 18px', gap: '10px' }}>
          <div className="bx-hstack" style={{ gap: '6px' }}>
            <span className="bx-badge">{TYPE_LABEL[item.type]()}</span>
            <span className="bx-badge bx-badge--gold">{t('app.wn.important', 'Important')}</span>
          </div>
          <h2 className="shl2-news-t" id="news-t2">
            {item.title}
          </h2>
          <p style={{ color: 'var(--ink-2)', maxWidth: '620px' }}>{item.summary}</p>
          {when ? (
            <div className="shl2-meta">
              <div className="shl2-meta-it">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M4 6h16v14H4zM4 10h16M8 3v4M16 3v4" />
                </svg>
                <div>
                  <b>
                    {when} · <span className="bx-m">{time}</span>
                    {mins ? (
                      <>
                        {' · '}
                        {rich((m) => t('app.wn.n-min', '{n} min', { n: m(<span className="bx-m">{mins}</span>) }))}
                      </>
                    ) : null}
                  </b>
                  <span className="bx-cap">{t('app.wn.your-tz', 'Your time zone, {tz}', { tz })}</span>
                </div>
              </div>
            </div>
          ) : null}
        </div>
        <div className="bx-dialog-f" style={{ padding: '12px 24px 16px' }}>
          <span className="lead">{t('app.wn.shown-once', "Shown once · you can find it later in What's New")}</span>
          <button type="button" className="bx-btn" onClick={() => close('remind_later')}>
            {t('app.wn.remind', 'Remind me later')}
          </button>
          {cta ? (
            <a
              className="bx-btn bx-btn--primary"
              href={external ? cta.url : '/whats-new-detail?id=' + encodeURIComponent(item.id)}
              target={external ? '_blank' : undefined}
              rel={external ? 'noopener noreferrer' : undefined}
              onClick={() => close('dismiss')}
            >
              {cta.label}
            </a>
          ) : (
            <a
              className="bx-btn bx-btn--primary"
              href={'/whats-new-detail?id=' + encodeURIComponent(item.id)}
              onClick={() => close('dismiss')}
            >
              {t('app.wn.open', 'Open')}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
